import React, { useEffect, useRef } from "react";

interface LocateDeviceOverlayProps {
  open: boolean;
  deviceCode?: string;
  durationMs?: number;
  onClose: () => void;
}

const DEFAULT_DURATION_MS = 8000;

const LocateDeviceOverlay: React.FC<LocateDeviceOverlayProps> = ({
  open,
  deviceCode,
  durationMs = DEFAULT_DURATION_MS,
  onClose,
}) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);

  const playBell = async () => {
    try {
      const AudioContextCtor =
        window.AudioContext || (window as any).webkitAudioContext;

      if (!AudioContextCtor) return;

      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioContextCtor();
      }

      const ctx = audioCtxRef.current;

      if (ctx.state === "suspended") {
        await ctx.resume();
      }

      const now = ctx.currentTime;

      // Bell-like double tone
      const playTone = (start: number, freq: number, length = 0.35) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, start);

        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + length);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(start);
        osc.stop(start + length + 0.05);
      };

      playTone(now, 880, 0.35);
      playTone(now + 0.18, 1320, 0.45);
      playTone(now + 0.55, 880, 0.35);
      playTone(now + 0.73, 1320, 0.45);
    } catch (error) {
      console.error("Failed to play locate bell:", error);
    }
  };

  useEffect(() => {
    if (!open) return;

    playBell();

    timeoutRef.current = setTimeout(() => {
      onClose();
    }, durationMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [open, durationMs, onClose]);

  useEffect(() => {
    return () => {
      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        audioCtxRef.current.close().catch(() => {});
      }
    };
  }, []);

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100000,
        background: "rgba(0, 0, 0, 0.88)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          textAlign: "center",
          color: "#fff",
          padding: "32px 40px",
          borderRadius: "24px",
          border: "4px solid #facc15",
          background: "rgba(255,255,255,0.08)",
          boxShadow: "0 0 40px rgba(250, 204, 21, 0.45)",
          minWidth: "50vw",
        }}
      >
        <div
          style={{
            fontSize: "5rem",
            lineHeight: 1,
            marginBottom: "1rem",
            animation: "locate-pulse 1s ease-in-out infinite",
          }}
        >
          🔔
        </div>

        <div
          style={{
            fontSize: "2.8rem",
            fontWeight: 800,
            letterSpacing: "0.04em",
            marginBottom: "0.75rem",
          }}
        >
          LOCATING DEVICE
        </div>

        <div
          style={{
            fontSize: "2rem",
            fontWeight: 600,
            color: "#fde68a",
          }}
        >
          {deviceCode ? deviceCode : "This Device"}
        </div>

        <style>
          {`
            @keyframes locate-pulse {
              0% { transform: scale(1); opacity: 1; }
              50% { transform: scale(1.18); opacity: 0.75; }
              100% { transform: scale(1); opacity: 1; }
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default LocateDeviceOverlay;